/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// @react-jvectormap components
import { VectorMap } from "@react-jvectormap/core";
import { worldMerc } from "@react-jvectormap/world";

import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import LinkIcon from "@mui/icons-material/Link";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DefaultInfoCard from "examples/Cards/InfoCards/DefaultInfoCard";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SalesTable from "examples/Tables/SalesTable";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import PieChart from "examples/Charts/PieChart";
import DataTable from "examples/Tables/DataTable";
import Footer from "examples/Footer";
import DefaultItem from "examples/Items/DefaultItem";

import PropTypes from "prop-types";

import { serverBaseURL, googleMapKey } from "common/settings";
import { caculateLifetime } from "common/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import SG from "assets/images/icons/flags/SG.png";
import JP from "assets/images/icons/flags/JP.png";

function GeoMap({ markers }) {
  return (
    <LoadScript googleMapsApiKey={googleMapKey}>
      <GoogleMap
        center={{ lat: 1.29, lng: 103.85 }}
        zoom={10}
        options={{ scrollwheel: false }}
        mapContainerStyle={{ height: `280px`, borderRadius: "6px", overflow: "hidden" }}
      >
        {markers}
      </GoogleMap>
    </LoadScript>
  );
}

GeoMap.propTypes = {
  markers: PropTypes.arrayOf(PropTypes.object).isRequired,
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Stack
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      mt={3}
      justifyContent="center"
      {...other}
    >
      {value === index && children}
    </Stack>
  );
}

TabPanel.defaultProps = {
  children: "",
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function Analysis() {
  const { getToken } = useKindeAuth();
  const navigate = useNavigate();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [processTime, setProcessTime] = useState("");
  const [status, setStatus] = useState("");
  const [mapTableDataProduction, setMapTableDataProduction] = useState([
    {
      country: ["", ""],
      red: "",
      yellow: "",
      green: "",
    },
  ]);
  const [timeLineDataProduction, setTimeLineDataProduction] = useState({});
  const [pieChartDataProduction, setPieChartDataProduction] = useState({
    labels: ["Red", "Yellow", "Green"],
    datasets: {
      label: "Breakdown",
      backgroundColors: ["error", "warning", "success"],
      data: [0, 0, 0],
    },
  });
  const [tableData, setTableData] = useState({
    columns: [
      { Header: "Site Name", accessor: "name" },
      { Header: "Site Status", accessor: "status" },
      { Header: "Current PV (kW)", accessor: "power" },
      { Header: "Optimal PV (kW)", accessor: "optimization" },
      { Header: "Best / Worst", accessor: "score" },
      { Header: "Problems", accessor: "problems" },
      { Header: "Latest year degradation rate", accessor: "latestYearDegradationRate" },
      { Header: "Degradation P95 exceedance level", accessor: "degradationExceedanceLevel" },
      { Header: "Degradation Confidence level", accessor: "degradationConfidenceLevel" },
      { Header: "Degradation Confidence interval", accessor: "degradationConfidenceInterval" },
      { Header: "Cumulative degradation rate", accessor: "cumulativeDegradationRate" },
      {
        Header: "P50 insolation-weighted soiling ratio",
        accessor: "insolationWeightedSoilingRatio",
      },
      {
        Header: "68.2 confidence interval soiling ratio",
        accessor: "confidenceIntervalSoilingRatio",
      },
      { Header: "Lifetime", accessor: "lifetime" },
      { Header: "Grade", accessor: "grade" },
      { Header: "Category", accessor: "category" },
      { Header: "Power generation forecast", accessor: "powerGenerationForecast" },
      { Header: "Environmental value(mpts/kwh)", accessor: "environmentalValue" },
      { Header: "Financial value(€/kwh)", accessor: "financialValue" },
      { Header: "Action", accessor: "actions" },
    ],

    rows: [],
  });
  const [analysisSites, setAnalysisSites] = useState([]);
  const [exceedanceLevelMedian, setExceedanceLevelMedian] = useState(0);
  const [maxConfidenceLevel, setMaxConfidenceLevel] = useState(0);
  const [minConfidenceLevel, setMinConfidenceLevel] = useState(0);
  const [maxWeightedSoilingRatio, setMaxWeightedSoilingRatio] = useState(0);
  const [minWeightedSoilingRatio, setMinWeightedSoilingRatio] = useState(0);
  const [maxLifetime, setMaxLifetime] = useState(1999);
  const [minLifetime, setMinLifetime] = useState(2099);
  const [trendPowerGenerationDailyMax, setTrendPowerGenerationDailyMax] = useState("");
  const [trendPowerGenerationMonthlyMax, setTrendPowerGenerationMonthlyMax] = useState("");
  const [trendPowerGenerationDailyAverage, setTrendPowerGenerationDailyAverage] = useState("");
  const [trendPowerGenerationMonthlyAverage, setTrendPowerGenerationMonthlyAverage] = useState("");
  const [trendGraphData, setTrendGraphData] = useState({});
  const [markers, setMarkers] = useState([]);
  // const [yearArray, setYearArray] = useState([]);

  const renderProblems = (problems) => {
    const problemList = String(problems ?? "")
      .split(";")
      .map((problem) => problem.trim())
      .filter(Boolean);

    if (problemList.length === 0) {
      return "-";
    }

    return (
      <MDBox>
        {problemList.map((problem, index) => (
          <MDTypography key={index} variant="caption" display="block" color="text">
            {problem}
          </MDTypography>
        ))}
      </MDBox>
    );
  };

  const formatThreeDecimals = (value) => {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? value : numberValue.toFixed(3);
  };

  const flags = {
    Singapore: SG,
    Japan: JP,
  };

  async function fetchData() {
    const accessToken = await getToken();
    const [response, siteResponse] = await Promise.all([
      fetch(`${serverBaseURL}get_trend_result?system_type=ongrid`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => result.json()),
      fetch(`${serverBaseURL}sites`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((result) => result.json()),
    ]);
    console.log(response);
    const siteMap = new Map(siteResponse.map((site) => [String(site.id), site]));
    const analysisSites = response.sites.map((result) => ({
      ...result,
      ...siteMap.get(String(result.site)),
    }));
    const lifetimeYears = analysisSites.map((prop) => {
      const installedYear = Number(prop.installed_date.split(" ")[0].split("-")[0]);
      return installedYear + prop.exceedance_levels.length + prop.lifetime_rates.length;
    });
    console.log(lifetimeYears);
    setMinLifetime(Math.min(...lifetimeYears));
    setMaxLifetime(Math.max(...lifetimeYears));

    setAnalysisSites(
      analysisSites.map((site) => ({
        ...site,
      }))
    );
    const iconBase = "http://maps.google.com/mapfiles/ms/icons/";
    setMarkers(
      analysisSites.map((prop) => {
        const color = prop.status;
        const { coordinate } = prop;
        const [lat, lng] = coordinate.split(",");
        let iconUrl;
        if (color === "Red") {
          iconUrl = `${iconBase}red-dot.png`;
        } else if (color === "Yellow") {
          iconUrl = `${iconBase}yellow-dot.png`;
        } else {
          iconUrl = `${iconBase}green-dot.png`;
        }
        return (
          <Marker
            position={{ lat: Number(lat), lng: Number(lng) }}
            icon={iconUrl}
            key={prop.name}
          />
        );
      })
    );

    const median = (arr) => {
      const { length } = arr;

      arr.sort((a, b) => a - b);

      if (length % 2 === 0) {
        return (arr[length / 2 - 1] + arr[length / 2]) / 2;
      }

      return arr[(length - 1) / 2];
    };

    const exceedanceLavelArray = analysisSites.map((site) => site.exceedance_level);

    setExceedanceLevelMedian(median(exceedanceLavelArray).toFixed(3));

    const confidenceLevelArray = analysisSites.map((site) => site.confidence_level);

    setMaxConfidenceLevel(Math.max(...confidenceLevelArray).toFixed(3));
    setMinConfidenceLevel(Math.min(...confidenceLevelArray).toFixed(3));

    const weightedSoilingRatioArray = analysisSites.map(
      (site) => site.insolation_weighted_soiling_ratio
    );
    setMaxWeightedSoilingRatio(Math.max(...weightedSoilingRatioArray).toFixed(3));
    setMinWeightedSoilingRatio(Math.min(...weightedSoilingRatioArray).toFixed(3));

    const mapTableDataProductionList = response.analysis.production_statistics.map((prop) => ({
      country: [flags[prop[0]], prop[0]],
      red: prop[1],
      yellow: prop[2],
      green: prop[3],
    }));
    setMapTableDataProduction(mapTableDataProductionList);
    setTimeLineDataProduction({
      labels: response.analysis.trend_production_graph_data.labels.map((i) => i.split(" ")[0]),
      datasets: [
        {
          label: "Red Trend",
          color: "error",
          data: response.analysis.trend_production_graph_data.series[0],
        },
        {
          label: "Yellow Trend",
          color: "warning",
          data: response.analysis.trend_production_graph_data.series[1],
        },
        {
          label: "Green Trend",
          color: "success",
          data: response.analysis.trend_production_graph_data.series[2],
        },
      ],
    });
    setPieChartDataProduction({
      labels: ["Red", "Yellow", "Green"],
      datasets: {
        label: "Breakdown",
        backgroundColors: ["error", "warning", "success"],
        data: [
          response.analysis.production_statistics[0][1],
          response.analysis.production_statistics[0][2],
          response.analysis.production_statistics[0][3],
        ],
      },
    });
    const ongridResultResponse = await (
      await fetch(`${serverBaseURL}ongrid_results/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    ).json();
    console.log(ongridResultResponse);
    setTrendPowerGenerationDailyMax(
      formatThreeDecimals(ongridResultResponse[0].overview.trend.daily_max)
    );
    setTrendPowerGenerationDailyAverage(
      formatThreeDecimals(ongridResultResponse[0].overview.trend.daily_mean)
    );
    setTrendPowerGenerationMonthlyMax(
      formatThreeDecimals(ongridResultResponse[0].overview.trend.monthly_max)
    );
    setTrendPowerGenerationMonthlyAverage(
      formatThreeDecimals(ongridResultResponse[0].overview.trend.monthly_mean)
    );
    setTrendGraphData({
      labels: ongridResultResponse[0].overview.trend.power_generation.label,
      datasets: [
        {
          label: "Power Generation",
          color: "success",
          data: ongridResultResponse[0].overview.trend.power_generation.series,
        },
      ],
    });
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setTableData({
      columns: [
        { Header: "Site Name", accessor: "name" },
        { Header: "Current PV (kW)", accessor: "power" },
        { Header: "Problems", accessor: "problems" },
        { Header: "Latest year degradation rate", accessor: "latestYearDegradationRate" },
        { Header: "Degradation P95 exceedance level", accessor: "degradationExceedanceLevel" },
        { Header: "Degradation Confidence level", accessor: "degradationConfidenceLevel" },
        { Header: "Degradation Confidence interval", accessor: "degradationConfidenceInterval" },
        { Header: "Cumulative degradation rate", accessor: "cumulativeDegradationRate" },
        {
          Header: "P50 insolation-weighted soiling ratio",
          accessor: "insolationWeightedSoilingRatio",
        },
        {
          Header: "68.2 confidence interval soiling ratio",
          accessor: "confidenceIntervalSoilingRatio",
        },
        { Header: "Lifetime", accessor: "lifetime" },
        { Header: "Grade", accessor: "grade" },
        { Header: "Category", accessor: "category" },
        { Header: "Power generation forecast", accessor: "powerGenerationForecast" },
        { Header: "Environmental value(mpts/kwh)", accessor: "environmentalValue" },
        { Header: "Financial value(€/kwh)", accessor: "financialValue" },
        { Header: "Action", accessor: "actions" },
      ],

      rows: analysisSites.map((prop, key) => ({
        id: key,
        name: prop.name,
        power: prop.panel_output,
        problems: renderProblems(prop.problems),
        /*
        latestYearDegradationRate: (
          <MDButton
            onClick={() => {
              navigate("/ongrid-dashboards/analysis-detail", { state: { name: prop.name } });
            }}
          >
            {prop.exceedance_levels.slice(-1)[0][1].toFixed(3)}%/yr
          </MDButton>
        ),
        */
        latestYearDegradationRate: `${prop.exceedance_levels.slice(-1)[0][1].toFixed(3)}%/yr`,
        /*
        degradationExceedanceLevel: (
          <MDButton
            onClick={() => {
              navigate("/ongrid-dashboards/analysis-detail", { state: { name: prop.name } });
            }}
          >
            {prop.exceedance_level.toFixed(3)}%/yr
          </MDButton>
        ),
        */
        degradationExceedanceLevel: `${prop.exceedance_level.toFixed(3)}%/yr`,
        /*
        degradationConfidenceLevel: (
          <MDButton
            onClick={() => {
              navigate("/ongrid-dashboards/analysis-detail", { state: { name: prop.name } });
            }}
          >
            {prop.confidence_level.toFixed(3)}%/yr
          </MDButton>
        ),
        */
        degradationConfidenceLevel: `${prop.confidence_level.toFixed(3)}%/yr`,
        /*
        degradationConfidenceInterval: (
          <MDButton
            onClick={() => {
              navigate("/ongrid-dashboards/analysis-detail", { state: { name: prop.name } });
            }}
          >
            {prop.confidence_interval[0].toFixed(3)} to {prop.confidence_interval[1].toFixed(3)}
          </MDButton>
        ),
        */
        degradationConfidenceInterval: `${prop.confidence_interval[0].toFixed(
          3
        )} to ${prop.confidence_interval[1].toFixed(3)}`,
        /*
        cumulativeDegradationRate: (
          <MDButton
            onClick={() => {
              navigate("/ongrid-dashboards/analysis-detail", { state: { name: prop.name } });
            }}
          >
            {prop.exceedance_levels
              .map((i) => i[1])
              .reduce((r, i) => r + i, 0)
              .toFixed(3)}
            %
          </MDButton>
        ),
        */
        cumulativeDegradationRate: `${prop.exceedance_levels
          .map((i) => i[1])
          .reduce((r, i) => r + i, 0)
          .toFixed(3)} %`,
        /*
        insolationWeightedSoilingRatio: (
          <MDButton
            onClick={() => {
              navigate("/ongrid-dashboards/analysis-detail", { state: { name: prop.name } });
            }}
          >
            {prop.insolation_weighted_soiling_ratio.toFixed(3)}
          </MDButton>
        ),
        */
        insolationWeightedSoilingRatio: prop.insolation_weighted_soiling_ratio.toFixed(3),
        /*
        confidenceIntervalSoilingRatio: (
          <MDButton
            onClick={() => {
              navigate("/ongrid-dashboards/analysis-detail", { state: { name: prop.name } });
            }}
          >
            {prop.confidence_interval_soiling_ratio[0].toFixed(3)}-
            {prop.confidence_interval_soiling_ratio[1].toFixed(3)}
          </MDButton>
        ),
        */
        confidenceIntervalSoilingRatio: `${prop.confidence_interval_soiling_ratio[0].toFixed(
          3
        )}-${prop.confidence_interval_soiling_ratio[1].toFixed(3)}`,
        /*
        lifetime: (
          <MDButton
            onClick={() => {
              navigate("/ongrid-dashboards/lifetime", {
                state: {
                  prop,
                },
              });
            }}
          >
            {caculateLifetime(prop)}
          </MDButton>
        ),
        */
        lifetime: caculateLifetime(prop),
        grade: prop.grade,
        category: prop.category,
        powerGenerationForecast: (
          <div className="actions-right">
            <MDButton
              onClick={() => {
                navigate("/power-generation-forecast", {
                  state: {
                    prop,
                  },
                });
              }}
            >
              <Icon fontSize="medium" color="secondary">
                open_in_new
              </Icon>
            </MDButton>
          </div>
        ),
        environmentalValue: (
          <MDButton
            onClick={() => {
              navigate("/environmental-financial", {
                state: {
                  prop,
                },
              });
            }}
          >
            {prop.environmental_value.toFixed(3)}
            <LinkIcon fontSize="small" sx={{ ml: 0.5 }} />
          </MDButton>
        ),
        financialValue: (
          <MDButton
            onClick={() => {
              navigate("/environmental-financial", {
                state: {
                  prop,
                },
              });
            }}
          >
            {prop.financial_value.toFixed(3)}
            <LinkIcon fontSize="small" sx={{ ml: 0.5 }} />
          </MDButton>
        ),
        actions: (
          <div className="actions-right">
            <MDButton
              onClick={() => {
                const [lat, lng] = prop.coordinate.split(",");
                const markerArray = [];
                markerArray.push([prop.status, lat, lng]);
                navigate("/dashboards/map", { state: { markerData: markerArray } });
              }}
            >
              <Icon fontSize="medium" color="primary">
                map
              </Icon>
            </MDButton>
          </div>
        ),
      })),
    });
  }, [analysisSites]);

  useEffect(() => {
    async function fetchTaskStatus() {
      const accessToken = await getToken();
      const statusResponse = await (
        await fetch(`${serverBaseURL}caculate_status`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
      ).json();
      setStatus(statusResponse.task_status);
      setStartTime(statusResponse.start_time.split(".")[0]);
      setEndTime(statusResponse.end_time.split(".")[0]);
      setProcessTime(statusResponse.process_time);
    }
    fetchTaskStatus();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        <br />
        <Grid container alignItems="center">
          <Grid item xs={12} md={12}>
            <Card sx={{ height: "100%" }}>
              <MDBox pt={2} px={2} lineHeight={1}>
                <MDTypography variant="h6" fontWeight="medium">
                  Latest Task
                </MDTypography>
              </MDBox>
              <MDBox p={2}>
                <DefaultItem color="dark" icon="tasks" title="Status" description={status} />
                <MDBox mt={2.5}>
                  <DefaultItem
                    color="dark"
                    icon="notifications_active"
                    title="Start Time"
                    description={startTime}
                  />
                </MDBox>
                <MDBox mt={2.5}>
                  <DefaultItem
                    color="dark"
                    icon="notifications_active"
                    title="End Time"
                    description={endTime}
                  />
                </MDBox>
                <MDBox mt={2.5}>
                  <DefaultItem
                    color="dark"
                    icon="notifications_active"
                    title="Process Time"
                    description={processTime}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <Grid container alignItems="center">
          <Grid item xs={12} md={7}>
            <MDBox mt={3} mb={1}>
              <MDTypography variant="h5">Life time trend</MDTypography>
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Median Degradation P95 Exceedance Level"
                  description=""
                  value={exceedanceLevelMedian}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Maximum Degradation Confidence Level"
                  description=""
                  value={maxConfidenceLevel}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Minimum Degradation Confidence Level"
                  description=""
                  value={minConfidenceLevel}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Maximum P50 insolation-weighted soiling ratio"
                  description=""
                  value={maxWeightedSoilingRatio}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Minimum P50 insolation-weighted soiling ratio"
                  description=""
                  value={minWeightedSoilingRatio}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Maximum Lifetime"
                  description=""
                  value={maxLifetime}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="grade"
                  title="Minimum Lifetime"
                  description=""
                  value={minLifetime}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Card sx={{ width: "100%" }}>
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  assignment
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                Site Status Table
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12}>
                  <DataTable table={tableData} canSearch />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
          <br />
        </MDBox>
        <Grid container alignItems="center">
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mt={3} mb={1}>
              <MDTypography variant="h5">System Status - All Regions</MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4} />
          <Grid item xs={12} md={6} lg={4} sx={{ ml: "auto" }} />
        </Grid>
        <MDBox mt={5} mb={1}>
          <Card sx={{ width: "100%" }}>
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  language
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                System Status
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container>
                <Grid item xs={12} md={7} lg={6}>
                  <SalesTable rows={mapTableDataProduction} shadow={false} />
                </Grid>
                <Grid item xs={12} md={5} lg={6} sx={{ mt: { xs: 5, lg: 0 } }}>
                  <VectorMap
                    map={worldMerc}
                    zoomOnScroll={false}
                    zoomButtons
                    focusOn={{
                      x: 0.5,
                      y: 0.5,
                      scale: 1,
                    }}
                    markersSelectable
                    backgroundColor="transparent"
                    markers={[
                      {
                        name: "Singapore",
                        latLng: [1.29027, 103.851959],
                      },
                    ]}
                    regionStyle={{
                      initial: {
                        fill: "#dee2e7",
                        "fill-opacity": 1,
                        stroke: "none",
                        "stroke-width": 0,
                        "stroke-opacity": 0,
                      },
                    }}
                    markerStyle={{
                      initial: {
                        fill: "#e91e63",
                        stroke: "#ffffff",
                        "stroke-width": 5,
                        "stroke-opacity": 0.5,
                        r: 7,
                      },
                      hover: {
                        fill: "E91E63",
                        stroke: "#ffffff",
                        "stroke-width": 5,
                        "stroke-opacity": 0.5,
                      },
                      selected: {
                        fill: "E91E63",
                        stroke: "#ffffff",
                        "stroke-width": 5,
                        "stroke-opacity": 0.5,
                      },
                    }}
                    style={{
                      marginTop: "-1.5rem",
                    }}
                    onRegionTipShow={() => false}
                    onMarkerTipShow={() => false}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>
        <Grid container alignItems="center">
          <Grid item xs={12} md={7}>
            <MDBox mt={3} mb={1}>
              <MDTypography variant="h5">System Status - By Country</MDTypography>
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5} mb={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={7}>
              <DefaultLineChart
                icon={{ component: "show_chart" }}
                title="Time Series"
                description="&nbsp;"
                chart={timeLineDataProduction}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={5}>
              <PieChart
                icon={{ color: "success", component: "donut_small" }}
                title="Pie chart"
                description="Analytics Insights"
                chart={pieChartDataProduction}
              />
            </Grid>
          </Grid>
        </MDBox>
        <Grid container alignItems="center">
          <Grid item xs={12} md={7}>
            <MDBox mb={1}>
              <MDTypography variant="h5">Historical Analysis</MDTypography>
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="ev_station"
                  title="Power Generation"
                  description="Daily max"
                  value={trendPowerGenerationDailyMax}
                  color="warning"
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="ev_station"
                  title="Power Generation"
                  description="Monthly max"
                  value={trendPowerGenerationMonthlyMax}
                  color="warning"
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="ev_station"
                  title="Power Generation"
                  description="Daily average"
                  value={trendPowerGenerationDailyAverage}
                  color="warning"
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultInfoCard
                  icon="ev_station"
                  title="Power Generation"
                  description="Monthly average"
                  value={trendPowerGenerationMonthlyAverage}
                  color="warning"
                />
              </MDBox>
            </Grid>
          </Grid>
          <br />
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultLineChart
                  icon={{ color: "warning", component: "show_chart" }}
                  title="Trend Graph"
                  description="&nbsp;"
                  chart={trendGraphData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Card sx={{ width: "100%" }}>
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  map
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                Status Map
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12}>
                  <GeoMap markers={markers} />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analysis;
